import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { endpoints, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import QrClockIn from "./QrClockIn";
import {
    PencilSimpleLine, Clock, Calendar, User, Briefcase, Coffee, TrendUp,
    PaperPlaneTilt, CircleNotch, CheckCircle, XCircle, WarningCircle, Airplane, ArrowsClockwise,
    Rows, CaretLeft, CaretRight, Copy, QrCode,
} from "@phosphor-icons/react";

const TIME_OFF_TYPES = [
    { value: "holiday", label: "Holiday" },
    { value: "sick", label: "Sick" },
    { value: "lateness", label: "Lateness" },
    { value: "compassionate", label: "Compassionate" },
    { value: "other", label: "Other" },
];

const STATUS_CFG = {
    pending: { label: "Pending", chip: "chip-orange", Icon: WarningCircle },
    approved: { label: "Approved", chip: "chip-green", Icon: CheckCircle },
    rejected: { label: "Rejected", chip: "chip-gray", Icon: XCircle },
};

const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const todayISO = () => new Date().toISOString().slice(0, 10);

function isoLocal(d) {
    const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}
function mondayOf(date) {
    const x = new Date(date);
    const off = (x.getDay() + 6) % 7; // 0 = Monday
    x.setDate(x.getDate() - off);
    x.setHours(0, 0, 0, 0);
    return x;
}
function fmtDayLabel(d) { return d.toLocaleDateString(undefined, { day: "numeric", month: "short" }); }

function matchEmployee(emps, user) {
    if (!user) return null;
    const linked = emps.find((e) => e.user_id && e.user_id === user.id);
    if (linked) return linked;
    const uName = (user.name || "").toLowerCase();
    if (!uName) return null;
    const exact = emps.find((e) => (e.employee_name || "").toLowerCase() === uName);
    if (exact) return exact;
    const uFirst = uName.split(" ")[0];
    return emps.find((e) => (e.employee_name || "").toLowerCase().split(" ")[0] === uFirst && uFirst.length >= 3) || null;
}

export default function CaptureTime() {
    const { user } = useAuth();
    const isManage = ["admin", "manager"].includes(user?.role);
    const [searchParams] = useSearchParams();
    const deepLinkCode = searchParams.get("code");

    const [mode, setMode] = useState(deepLinkCode ? "clockin" : "single"); // single | grid | clockin
    const [employees, setEmployees] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [projects, setProjects] = useState([]);
    const [matched, setMatched] = useState(null);
    const [loadingInit, setLoadingInit] = useState(true);
    const [entryType, setEntryType] = useState("work");
    const [submitting, setSubmitting] = useState(false);
    const [subs, setSubs] = useState([]);
    const [subLoading, setSubLoading] = useState(false);

    const [form, setForm] = useState({
        work_date: todayISO(), employee_id: "", job_id: "", type_raw: "",
        decimal_hours: "", shift_in: "", shift_out: "", break_hours: "",
        overtime_hours: "", time_off_type: "holiday", notes_raw: "",
    });
    const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    useEffect(() => {
        (async () => {
            try {
                const [empRes, jobRes, projRes] = await Promise.all([
                    endpoints.employees({ active_only: true }), endpoints.jobs(), endpoints.projects(),
                ]);
                const emps = empRes.data || [];
                setEmployees(emps);
                setJobs(jobRes.data || []);
                setProjects(projRes.data || []);
                const m = matchEmployee(emps, user);
                if (m) { setMatched(m); setF("employee_id", m.id); }
            } catch (e) { /* ignore */ }
            finally { setLoadingInit(false); }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const loadSubs = useCallback(async () => {
        setSubLoading(true);
        try {
            const { data } = await endpoints.timeEntries({ my_entries: true, per_page: 100 });
            setSubs(data?.items || []);
        } catch { /* ignore */ }
        finally { setSubLoading(false); }
    }, []);
    useEffect(() => { loadSubs(); }, [loadSubs]);

    async function submit() {
        if (!form.employee_id) { alert("Select an employee"); return; }
        if (!form.work_date) { alert("Select a date"); return; }
        const hours = parseFloat(form.decimal_hours);
        if (!hours || hours <= 0) { alert("Enter hours > 0"); return; }
        if (hours > 24) { alert("Hours cannot exceed 24"); return; }
        setSubmitting(true);
        const job = jobs.find((j) => j.id === form.job_id);
        try {
            await endpoints.createTimeEntry({
                employee_id: form.employee_id,
                work_date: form.work_date,
                entry_type: entryType,
                time_off_type: entryType === "time_off" ? form.time_off_type : "",
                type_raw: entryType === "work" ? form.type_raw : (TIME_OFF_TYPES.find((t) => t.value === form.time_off_type)?.label || ""),
                job_id: entryType === "work" ? (form.job_id || null) : null,
                job_ref: job?.ref || "",
                decimal_hours: hours,
                shift_in: form.shift_in, shift_out: form.shift_out,
                break_hours: parseFloat(form.break_hours) || 0,
                overtime_hours: parseFloat(form.overtime_hours) || 0,
                notes_raw: form.notes_raw,
            });
            setForm((f) => ({ ...f, type_raw: "", decimal_hours: "", shift_in: "", shift_out: "", break_hours: "", overtime_hours: "", notes_raw: "", job_id: "" }));
            loadSubs();
        } catch (e) { alert(formatApiError(e)); }
        finally { setSubmitting(false); }
    }

    const empOptions = useMemo(() => employees.map((e) => ({ value: e.id, label: e.employee_name })), [employees]);

    if (loadingInit) return <div className="max-w-3xl mx-auto px-6 py-12 text-center text-[var(--rom-text-3)]">Loading…</div>;

    return (
        <div className="max-w-4xl mx-auto px-6 py-8 space-y-6" data-testid="capture-time-page">
            <header>
                <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--rom-text-3)] font-semibold mb-1">Timesheets</div>
                <h1 className="font-display font-black text-3xl tracking-tight flex items-center gap-3">
                    <PencilSimpleLine size={28} weight="bold" className="text-[var(--rom-orange)]" /> Capture Time
                </h1>
                <p className="text-sm text-[var(--rom-text-2)] mt-1">Log time worked or request time off. Submissions go to a manager for approval.</p>
            </header>

            {/* Mode toggle: Single Entry / Weekly Grid / QR Clock-in */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex gap-0 rounded-[8px] overflow-hidden border border-[var(--rom-border)] w-fit p-1 bg-slate-50" data-testid="capture-mode-toggle">
                    <button data-testid="mode-single" onClick={() => setMode("single")}
                        className={`flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-[6px] transition-colors ${mode === "single" ? "bg-white text-[var(--rom-navy)] shadow-sm" : "text-[var(--rom-text-2)] hover:text-[var(--rom-text)]"}`}>
                        <PencilSimpleLine size={14} weight="bold" /> Single Entry
                    </button>
                    <button data-testid="mode-grid" onClick={() => setMode("grid")}
                        className={`flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-[6px] transition-colors ${mode === "grid" ? "bg-[var(--rom-navy)] text-white shadow-sm" : "text-[var(--rom-text-2)] hover:text-[var(--rom-text)]"}`}>
                        <Rows size={14} weight="bold" /> Weekly Grid
                    </button>
                    <button data-testid="mode-clockin" onClick={() => setMode("clockin")}
                        className={`flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-[6px] transition-colors ${mode === "clockin" ? "bg-[var(--rom-orange)] text-white shadow-sm" : "text-[var(--rom-text-2)] hover:text-[var(--rom-text)]"}`}>
                        <QrCode size={14} weight="bold" /> QR Clock-in
                    </button>
                </div>
                {mode === "grid" && <span className="text-sm text-[var(--rom-text-3)]">Fill in your whole week and submit in one go</span>}
                {mode === "clockin" && <span className="text-sm text-[var(--rom-text-3)]">Site register — for crew and subcontractors arriving on site</span>}
            </div>

            {mode === "single" ? (
                <SingleEntry
                    isManage={isManage} matched={matched} entryType={entryType} setEntryType={setEntryType}
                    empOptions={empOptions} jobs={jobs} form={form} setF={setF} submit={submit} submitting={submitting}
                />
            ) : mode === "grid" ? (
                <WeeklyGrid
                    isManage={isManage} matched={matched} empOptions={empOptions} projects={projects}
                    onSubmitted={loadSubs}
                />
            ) : (
                <QrClockIn employees={employees} projects={projects} initialCode={deepLinkCode} />
            )}

            {mode !== "clockin" && <MySubmissions subs={subs} loading={subLoading} reload={loadSubs} />}
        </div>
    );
}

/* ───────────────────────── Single entry ───────────────────────── */
function SingleEntry({ isManage, matched, entryType, setEntryType, empOptions, jobs, form, setF, submit, submitting }) {
    return (
        <>
            <div className="flex gap-0 rounded-[6px] overflow-hidden border border-[var(--rom-border)] w-fit">
                <button data-testid="entry-type-work" onClick={() => setEntryType("work")}
                    className={`flex items-center gap-2 px-5 py-2.5 text-sm font-semibold transition-colors ${entryType === "work" ? "bg-[var(--rom-navy)] text-white" : "bg-white text-[var(--rom-text-2)] hover:bg-slate-50"}`}>
                    <Clock size={14} weight="bold" /> Log Time Worked
                </button>
                <button data-testid="entry-type-time-off" onClick={() => setEntryType("time_off")}
                    className={`flex items-center gap-2 px-5 py-2.5 text-sm font-semibold transition-colors border-l border-[var(--rom-border)] ${entryType === "time_off" ? "bg-[var(--rom-orange)] text-white" : "bg-white text-[var(--rom-text-2)] hover:bg-slate-50"}`}>
                    <Airplane size={14} weight="bold" /> Request Time Off
                </button>
            </div>

            <div className="rom-card">
                <div className="rom-card-body grid grid-cols-2 gap-4">
                    <Field label={<><Calendar size={11} className="inline mr-1" />Work date *</>}>
                        <input data-testid="capture-date" type="date" value={form.work_date} onChange={(e) => setF("work_date", e.target.value)} className={inputCls} />
                    </Field>
                    <Field label={<><User size={11} className="inline mr-1" />Employee *</>}>
                        {!isManage && matched ? (
                            <div data-testid="capture-employee-locked" className="h-9 px-3 flex items-center rounded-[4px] border border-[var(--rom-border)] bg-slate-50 text-sm">
                                {matched.employee_name}<span className="ml-auto text-xs text-[var(--rom-text-3)]">you</span>
                            </div>
                        ) : !isManage && !matched ? (
                            <div className="h-9 px-3 flex items-center rounded-[4px] border border-amber-300 bg-amber-50 text-xs text-amber-700">No matching employee record. Ask an admin.</div>
                        ) : (
                            <select data-testid="capture-employee-select" value={form.employee_id} onChange={(e) => setF("employee_id", e.target.value)} className={inputCls}>
                                <option value="">Select employee…</option>
                                {empOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        )}
                    </Field>

                    {entryType === "work" && (<>
                        <Field label={<><Briefcase size={11} className="inline mr-1" />Project / ROM (optional)</>}>
                            <select data-testid="capture-project" value={form.job_id} onChange={(e) => setF("job_id", e.target.value)} className={inputCls}>
                                <option value="">Unallocated / general</option>
                                {jobs.map((j) => <option key={j.id} value={j.id}>{j.ref} · {j.loss_type}</option>)}
                            </select>
                        </Field>
                        <Field label="Work type / description">
                            <input data-testid="capture-type" value={form.type_raw} onChange={(e) => setF("type_raw", e.target.value)} placeholder="e.g. Tear-out, drying setup" className={inputCls} />
                        </Field>
                    </>)}

                    {entryType === "time_off" && (
                        <Field label="Type of leave *" span2>
                            <div className="flex flex-wrap gap-2">
                                {TIME_OFF_TYPES.map((t) => (
                                    <button key={t.value} data-testid={`time-off-type-${t.value}`} onClick={() => setF("time_off_type", t.value)}
                                        className={`px-3 py-1.5 rounded-[4px] text-xs font-semibold border transition-colors ${form.time_off_type === t.value ? "bg-[var(--rom-orange)] text-white border-[var(--rom-orange)]" : "bg-white text-[var(--rom-text-2)] border-[var(--rom-border)] hover:border-[var(--rom-orange)]"}`}>
                                        {t.label}
                                    </button>
                                ))}
                            </div>
                        </Field>
                    )}

                    <Field label={<><Clock size={11} className="inline mr-1" />Hours *</>}>
                        <input data-testid="capture-hours" type="number" step="0.25" min="0.25" max="24" value={form.decimal_hours} onChange={(e) => setF("decimal_hours", e.target.value)} placeholder="e.g. 7.5" className={inputCls} />
                    </Field>

                    {entryType === "work" && (<>
                        <Field label="Shift in / out (optional)">
                            <div className="flex gap-2">
                                <input data-testid="capture-shift-in" type="time" value={form.shift_in} onChange={(e) => setF("shift_in", e.target.value)} className={`${inputCls} flex-1`} />
                                <input data-testid="capture-shift-out" type="time" value={form.shift_out} onChange={(e) => setF("shift_out", e.target.value)} className={`${inputCls} flex-1`} />
                            </div>
                        </Field>
                        <Field label={<><Coffee size={11} className="inline mr-1" />Break (hrs)</>}>
                            <input data-testid="capture-break" type="number" step="0.25" min="0" value={form.break_hours} onChange={(e) => setF("break_hours", e.target.value)} placeholder="0.5" className={inputCls} />
                        </Field>
                        <Field label={<><TrendUp size={11} className="inline mr-1" />Overtime (hrs)</>}>
                            <input data-testid="capture-overtime" type="number" step="0.25" min="0" value={form.overtime_hours} onChange={(e) => setF("overtime_hours", e.target.value)} placeholder="1.0" className={inputCls} />
                        </Field>
                    </>)}

                    <Field label="Notes (optional)" span2>
                        <textarea data-testid="capture-notes" value={form.notes_raw} onChange={(e) => setF("notes_raw", e.target.value)} rows={2}
                            className="w-full px-3 py-2 rounded-[4px] border border-[var(--rom-border)] text-sm focus:border-[var(--rom-orange)] focus:outline-none resize-none" />
                    </Field>
                </div>
                <div className="px-5 pb-5">
                    <button data-testid="capture-submit" onClick={submit} disabled={submitting || (!isManage && !matched)} className="btn btn-primary w-full justify-center">
                        {submitting ? <CircleNotch size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} weight="bold" />} Submit for approval
                    </button>
                </div>
            </div>
        </>
    );
}

/* ───────────────────────── Weekly grid ───────────────────────── */
function blankWeek(weekStart, defaultProject) {
    const rows = {};
    for (let i = 0; i < 7; i++) {
        const dt = new Date(weekStart); dt.setDate(dt.getDate() + i);
        rows[isoLocal(dt)] = {
            active: i < 5, type: "work", desc: "", project_id: defaultProject || "",
            hours: i < 5 ? "8" : "", time_off_type: "holiday",
        };
    }
    return rows;
}

function WeeklyGrid({ isManage, matched, empOptions, projects, onSubmitted }) {
    const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
    const [employeeId, setEmployeeId] = useState(matched?.id || "");
    const [defaultProject, setDefaultProject] = useState("");
    const [grid, setGrid] = useState(() => blankWeek(mondayOf(new Date()), ""));
    const [busy, setBusy] = useState(false);
    const [copying, setCopying] = useState(false);
    const [msg, setMsg] = useState("");

    useEffect(() => { setGrid(blankWeek(weekStart, defaultProject)); /* eslint-disable-next-line */ }, [weekStart]);

    const days = useMemo(() => [...Array(7)].map((_, i) => {
        const dt = new Date(weekStart); dt.setDate(dt.getDate() + i);
        return { iso: isoLocal(dt), name: DAY_NAMES[i], label: fmtDayLabel(dt) };
    }), [weekStart]);

    const rangeLabel = days.length ? `${days[0].label} – ${days[6].label}` : "";
    const setRow = (iso, patch) => setGrid((g) => ({ ...g, [iso]: { ...g[iso], ...patch } }));

    function applyDefaultProject(pid) {
        setDefaultProject(pid);
        setGrid((g) => {
            const next = { ...g };
            for (const iso of Object.keys(next)) next[iso] = { ...next[iso], project_id: pid };
            return next;
        });
    }

    const activeRows = days.filter((d) => grid[d.iso]?.active);
    const totalHours = activeRows.reduce((s, d) => s + (parseFloat(grid[d.iso]?.hours) || 0), 0);
    const workCount = activeRows.filter((d) => grid[d.iso]?.type === "work").length;

    async function submitWeek() {
        if (!employeeId) { alert("Select an employee"); return; }
        const toSubmit = activeRows.filter((d) => (parseFloat(grid[d.iso]?.hours) || 0) > 0);
        if (toSubmit.length === 0) { alert("Add hours to at least one active day"); return; }
        setBusy(true); setMsg("");
        const batch = (crypto.randomUUID && crypto.randomUUID()) || `wk-${Date.now()}`;
        try {
            for (const d of toSubmit) {
                const r = grid[d.iso];
                const proj = projects.find((p) => p.id === r.project_id);
                await endpoints.createTimeEntry({
                    employee_id: employeeId,
                    work_date: d.iso,
                    entry_type: r.type,
                    time_off_type: r.type === "time_off" ? r.time_off_type : "",
                    type_raw: r.type === "work" ? r.desc : (TIME_OFF_TYPES.find((t) => t.value === r.time_off_type)?.label || ""),
                    project_id: r.type === "work" ? (r.project_id || null) : null,
                    project_name: r.type === "work" ? (proj?.name || "") : "",
                    decimal_hours: parseFloat(r.hours),
                    notes_raw: "",
                    week_batch_id: batch,
                });
            }
            setMsg(`Submitted ${toSubmit.length} day${toSubmit.length !== 1 ? "s" : ""} for approval as one weekly batch.`);
            setGrid(blankWeek(weekStart, defaultProject));
            onSubmitted?.();
        } catch (e) { alert(formatApiError(e)); }
        finally { setBusy(false); }
    }

    const lockedEmp = !isManage && matched;

    async function copyLastWeek() {
        if (!employeeId) { alert("Select an employee first"); return; }
        setCopying(true); setMsg("");
        const prevMon = new Date(weekStart); prevMon.setDate(prevMon.getDate() - 7);
        const prevSun = new Date(prevMon); prevSun.setDate(prevSun.getDate() + 6);
        try {
            const { data } = await endpoints.timeEntries({
                employee_id: employeeId, date_from: isoLocal(prevMon), date_to: isoLocal(prevSun), per_page: 100,
            });
            const items = data?.items || [];
            const byDow = {};
            for (const e of items) {
                const dt = new Date(e.work_date + "T00:00:00");
                const dow = (dt.getDay() + 6) % 7;
                if (!byDow[dow] || (e.decimal_hours || 0) > (byDow[dow].decimal_hours || 0)) byDow[dow] = e;
            }
            if (Object.keys(byDow).length === 0) { setMsg("No entries found for last week to copy."); setCopying(false); return; }
            const next = blankWeek(weekStart, defaultProject);
            for (const iso of Object.keys(next)) next[iso] = { ...next[iso], active: false, hours: "" };
            days.forEach((d, i) => {
                const e = byDow[i];
                if (!e) return;
                const isOff = e.entry_type === "time_off";
                const tot = TIME_OFF_TYPES.find((t) => t.value === e.time_off_type || t.label === e.time_off_type);
                next[d.iso] = {
                    active: true,
                    type: isOff ? "time_off" : "work",
                    desc: isOff ? "" : (e.type_raw || ""),
                    project_id: isOff ? "" : (e.project_id || ""),
                    hours: String(e.decimal_hours || ""),
                    time_off_type: isOff ? (tot?.value || "holiday") : "holiday",
                };
            });
            setGrid(next);
            setMsg(`Copied ${Object.keys(byDow).length} day${Object.keys(byDow).length !== 1 ? "s" : ""} from last week — review and submit.`);
        } catch (e) { alert(formatApiError(e)); }
        finally { setCopying(false); }
    }

    return (
        <div className="rom-card" data-testid="weekly-grid">
            <div className="rom-card-body space-y-5">
                {/* Week nav */}
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                        <button data-testid="week-prev" onClick={() => setWeekStart((w) => { const n = new Date(w); n.setDate(n.getDate() - 7); return n; })}
                            className="w-8 h-8 rounded-[6px] border border-[var(--rom-border)] flex items-center justify-center hover:bg-slate-50"><CaretLeft size={14} weight="bold" /></button>
                        <span className="font-display font-black text-lg" data-testid="week-range">{rangeLabel}</span>
                        <button data-testid="week-next" onClick={() => setWeekStart((w) => { const n = new Date(w); n.setDate(n.getDate() + 7); return n; })}
                            className="w-8 h-8 rounded-[6px] border border-[var(--rom-border)] flex items-center justify-center hover:bg-slate-50"><CaretRight size={14} weight="bold" /></button>
                    </div>
                    <div className="text-sm text-[var(--rom-text-2)] flex items-center gap-4">
                        <button data-testid="grid-copy-last-week" onClick={copyLastWeek} disabled={copying}
                            className="btn btn-ghost btn-sm" title="Pre-fill this week from your last week's entries">
                            {copying ? <CircleNotch size={13} className="animate-spin" /> : <Copy size={13} weight="bold" />} Copy last week
                        </button>
                        <span>
                            <span className="font-semibold">{activeRows.length}</span> days active ·
                            <span className="font-semibold text-[var(--rom-text)]"> {totalHours.toFixed(1)}h</span> total ·
                            <span className="text-[var(--rom-navy)] font-semibold"> {workCount} work</span>
                        </span>
                    </div>
                </div>

                {/* Employee + default project */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label={<><User size={11} className="inline mr-1" />Employee *</>}>
                        {lockedEmp ? (
                            <div data-testid="grid-employee-locked" className="h-9 px-3 flex items-center rounded-[4px] border border-[var(--rom-border)] bg-slate-50 text-sm">
                                {matched.employee_name}<span className="ml-auto text-xs text-[var(--rom-text-3)]">you</span>
                            </div>
                        ) : (
                            <select data-testid="grid-employee-select" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} className={inputCls}>
                                <option value="">Select employee…</option>
                                {empOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        )}
                    </Field>
                    <Field label={<><Briefcase size={11} className="inline mr-1" />Default project (optional)</>}>
                        <select data-testid="grid-default-project" value={defaultProject} onChange={(e) => applyDefaultProject(e.target.value)} className={inputCls}>
                            <option value="">No project / general</option>
                            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </Field>
                </div>

                {/* Grid */}
                <div className="border border-[var(--rom-border)] rounded-[8px] overflow-hidden">
                    <div className="grid grid-cols-[auto_120px_1fr_1fr_90px] gap-3 px-3 py-2.5 bg-[var(--rom-navy)] text-white text-[11px] uppercase tracking-wider font-bold">
                        <span>Day</span><span>Type</span><span>Description</span><span>Project</span><span className="text-right">Hours</span>
                    </div>
                    {days.map((d) => {
                        const r = grid[d.iso] || {};
                        const off = r.type === "time_off";
                        return (
                            <div key={d.iso} data-testid={`grid-row-${d.iso}`}
                                className={`grid grid-cols-[auto_120px_1fr_1fr_90px] gap-3 px-3 py-2.5 items-center border-t border-[var(--rom-border)] ${r.active ? "" : "opacity-50"}`}>
                                <label className="flex items-center gap-2 cursor-pointer min-w-[110px]">
                                    <input type="checkbox" checked={!!r.active} onChange={(e) => setRow(d.iso, { active: e.target.checked })} data-testid={`grid-active-${d.iso}`} />
                                    <span>
                                        <span className="block text-sm font-semibold leading-tight">{d.name}</span>
                                        <span className="block text-[11px] text-[var(--rom-text-3)]">{d.label}</span>
                                    </span>
                                </label>
                                <div className="flex rounded-[6px] overflow-hidden border border-[var(--rom-border)] text-[11px] font-semibold">
                                    <button disabled={!r.active} onClick={() => setRow(d.iso, { type: "work" })} data-testid={`grid-type-work-${d.iso}`}
                                        className={`flex-1 py-1.5 ${!off ? "bg-[var(--rom-navy)] text-white" : "bg-white text-[var(--rom-text-2)]"}`}>Work</button>
                                    <button disabled={!r.active} onClick={() => setRow(d.iso, { type: "time_off" })} data-testid={`grid-type-off-${d.iso}`}
                                        className={`flex-1 py-1.5 border-l border-[var(--rom-border)] ${off ? "bg-[var(--rom-orange)] text-white" : "bg-white text-[var(--rom-text-2)]"}`}>Off</button>
                                </div>
                                {off ? (
                                    <select disabled={!r.active} value={r.time_off_type} onChange={(e) => setRow(d.iso, { time_off_type: e.target.value })} data-testid={`grid-leave-${d.iso}`} className={`${inputCls} h-8`}>
                                        {TIME_OFF_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                    </select>
                                ) : (
                                    <input disabled={!r.active} value={r.desc} onChange={(e) => setRow(d.iso, { desc: e.target.value })} placeholder="Description…" data-testid={`grid-desc-${d.iso}`} className={`${inputCls} h-8`} />
                                )}
                                <select disabled={!r.active || off} value={r.project_id} onChange={(e) => setRow(d.iso, { project_id: e.target.value })} data-testid={`grid-project-${d.iso}`} className={`${inputCls} h-8`}>
                                    <option value="">No project</option>
                                    {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </select>
                                <input disabled={!r.active} type="number" step="0.25" min="0" max="24" value={r.hours} onChange={(e) => setRow(d.iso, { hours: e.target.value })} placeholder="0" data-testid={`grid-hours-${d.iso}`} className={`${inputCls} h-8 text-right`} />
                            </div>
                        );
                    })}
                </div>

                {msg && <div className="text-sm text-[var(--rom-green)] flex items-center gap-2" data-testid="grid-success"><CheckCircle size={15} weight="fill" /> {msg}</div>}

                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <span className="text-xs text-[var(--rom-text-3)]">{activeRows.length} active days · {totalHours.toFixed(1)}h total · submitted as one weekly batch for approval</span>
                    <button data-testid="grid-submit-week" onClick={submitWeek} disabled={busy || (!isManage && !matched)} className="btn btn-primary">
                        {busy ? <CircleNotch size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} weight="bold" />} Submit Week for Approval
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ───────────────────────── My submissions (filterable) ───────────────────────── */
const TYPE_FILTERS = [
    { v: "all", label: "All" },
    { v: "work", label: "Time Worked" },
    { v: "time_off", label: "Time Off" },
];
const PERIOD_FILTERS = [
    { v: "this_week", label: "This week" },
    { v: "last_week", label: "Last week" },
    { v: "this_month", label: "This month" },
    { v: "all", label: "All time" },
];

function inPeriod(dateStr, period) {
    if (period === "all") return true;
    if (!dateStr) return false;
    const d = new Date(dateStr + "T00:00:00");
    const now = new Date();
    if (period === "this_month") return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    const thisMon = mondayOf(now);
    if (period === "this_week") { const end = new Date(thisMon); end.setDate(end.getDate() + 6); return d >= thisMon && d <= end; }
    if (period === "last_week") { const start = new Date(thisMon); start.setDate(start.getDate() - 7); const end = new Date(thisMon); end.setDate(end.getDate() - 1); return d >= start && d <= end; }
    return true;
}

function MySubmissions({ subs, loading, reload }) {
    const [typeF, setTypeF] = useState("all");
    const [periodF, setPeriodF] = useState("this_week");

    const filtered = useMemo(() => subs.filter((e) => {
        if (typeF !== "all" && (e.entry_type || "work") !== typeF) return false;
        return inPeriod(e.work_date, periodF);
    }), [subs, typeF, periodF]);

    return (
        <div className="rom-card" data-testid="my-submissions">
            <div className="rom-card-head flex-wrap gap-3">
                <h3>My Submissions</h3>
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex gap-1" data-testid="submissions-type-filters">
                        {TYPE_FILTERS.map((t) => (
                            <button key={t.v} onClick={() => setTypeF(t.v)} data-testid={`subs-type-${t.v}`}
                                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${typeF === t.v ? "bg-[var(--rom-navy)] text-white" : "bg-slate-100 text-[var(--rom-text-2)] hover:bg-slate-200"}`}>
                                {t.label}
                            </button>
                        ))}
                    </div>
                    <div className="flex gap-1" data-testid="submissions-period-filters">
                        {PERIOD_FILTERS.map((p) => (
                            <button key={p.v} onClick={() => setPeriodF(p.v)} data-testid={`subs-period-${p.v}`}
                                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${periodF === p.v ? "bg-[var(--rom-orange)] text-white" : "bg-slate-100 text-[var(--rom-text-2)] hover:bg-slate-200"}`}>
                                {p.label}
                            </button>
                        ))}
                    </div>
                    <button onClick={reload} className="p-1.5 text-[var(--rom-text-3)] hover:text-[var(--rom-text)]"><ArrowsClockwise size={13} className={loading ? "animate-spin" : ""} /></button>
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="rom-table">
                    <thead><tr><th>Date</th><th>Type</th><th>Description</th><th>Project</th><th className="numeric">Hours</th><th>Status</th></tr></thead>
                    <tbody>
                        {filtered.length === 0 ? (
                            <tr><td colSpan={6} className="text-center py-8 text-[var(--rom-text-3)]" data-testid="submissions-empty">No submissions for this period.</td></tr>
                        ) : filtered.map((e) => {
                            const cfg = STATUS_CFG[e.approval_status] || STATUS_CFG.pending;
                            return (
                                <tr key={e.id} data-testid={`submission-row-${e.id}`}>
                                    <td className="font-mono text-xs">{e.work_date}</td>
                                    <td><span className={`chip ${e.entry_type === "time_off" ? "chip-orange" : "chip-navy"}`}>{e.entry_type === "time_off" ? "Time off" : "Work"}</span></td>
                                    <td className="text-[var(--rom-text-2)]">{e.type_raw || e.time_off_type || "—"}</td>
                                    <td className="text-[var(--rom-text-2)] text-xs">{e.project_name || e.job_ref || "General"}</td>
                                    <td className="numeric font-semibold">{(e.decimal_hours || 0).toFixed(1)}h</td>
                                    <td>
                                        <span className={`chip ${cfg.chip}`}><cfg.Icon size={11} weight="bold" /> {cfg.label}</span>
                                        {e.approval_status === "rejected" && e.approval_note && <div className="text-[11px] text-red-500 mt-0.5">{e.approval_note}</div>}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

const inputCls = "w-full h-9 px-3 rounded-[4px] border border-[var(--rom-border)] text-sm focus:border-[var(--rom-orange)] focus:outline-none bg-white";

function Field({ label, children, span2 }) {
    return (
        <div className={span2 ? "col-span-2" : ""}>
            <label className="block text-xs font-semibold text-[var(--rom-text-2)] mb-1">{label}</label>
            {children}
        </div>
    );
}
